import { FunctionComponent, useState, useEffect, SetStateAction } from 'react';
import MultipleDateSelector from '../DateSelector/MultipleDateSelector';
import PeriodSelector from '../DateSelector/PeriodSelector';
import { noop } from 'lodash';
import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import styles from './Daycare.module.scss';
import { DaycareService, DaycareServiceWrapper } from 'model/Service';
import DaycareServiceSelector from './UI/DaycareServiceSelector';
import RoomServiceSelector from './UI/RoomServiceSelector';
import { Room } from '@spike/room-model';
import Button from 'components/UI/V3/Button/Button';
import PriceHours from './UI/PriceHours';
import { ValueTypeSelector, ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';
import { Daycare as DaycareModel } from 'model/NewMultipleBooking';
import useNonInitialEffect from '@versiondos/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { PetsState, fetchPetCustomPricesThunk } from '@spike/pets-action';
import { RecentlyBooked } from '../UI/RecentlyBooked';
import { AppointmentHistoryDrawer } from '../UI/AppointmentHistoryDrawer';
import OrderDetail from 'components/OrderDetail';
import { AppointmentListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import lockStyles from '../UI/priceFieldInteractionLock.module.scss';
import { useApiClientWrapper, useMarketplace, usePermissions } from 'hooks';
import type { Enrollment, MembershipItem } from '@spike/membership-model';
import type { EnrollmentAnchor } from '../utils/enrollmentAnchor';
import { getTodayClosingTime } from 'components/CreateBooking/Memberships/utils/membershipItemFlow';
import { PetCustomPrice } from '@spike/pet-model';
import { resolveStandardPrice } from '../utils/bookingCustomPrices';
import SaveCustomPriceCheckbox from '../UI/SaveCustomPriceCheckbox';
import SavedCustomPriceChoice from '../UI/SavedCustomPriceChoice';
import { useSavedCustomPriceChoice } from '../UI/useSavedCustomPriceChoice';

interface DaycareProps {
    clientId: number;
    petId: number;
    daycare: DaycareModel;
    edit?: boolean;
    servicesUsed?: Array<number>;
    enrollment?: Enrollment;
    membershipItem?: MembershipItem;
    enrollmentAnchor?: EnrollmentAnchor;
    onNext: (daycare: DaycareModel) => void;
    onChange?: (daycare: DaycareModel, selectedDates: Array<Moment>) => void;
}

const getFee = (daycareData: DaycareModel, petType?: string) => {
    if (petType === 'dogs') {
        return daycareData.service?.latePickupFee?.fees?.dogs?.toFixed(2);
    } else if (petType === 'cats') {
        return daycareData.service?.latePickupFee?.fees?.cats?.toFixed(2);
    } else if (petType === 'exotics') {
        return daycareData.service?.latePickupFee?.fees?.exotics?.toFixed(2);
    }
};

export const Daycare: FunctionComponent<DaycareProps> = props => {
    const marketplace = useMarketplace();
    const tz = marketplace.schedule.timeZone;
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const permissions = usePermissions();
    const canApplyDiscount = permissions.checkout.applyDiscounts;
    const canEditPrices = permissions.checkout.editPrices;
    const [daycareData, setDaycareData] = useState<DaycareModel>(props.daycare);
    const [selectedDates, setSelectedDates] = useState<Array<Moment>>(
        props.daycare?.selectedDates?.length
            ? props.daycare.selectedDates.map(d => d.clone().tz(tz).startOf('day'))
            : props.daycare?.from
            ? [props.daycare.from.clone().tz(tz).startOf('day')]
            : []
    );
    const [firstLoad, setFirstLoad] = useState<boolean>(false);
    const [daycareLoaded, setDaycareLoaded] = useState<boolean>(false);
    const [showAppointmentHistoryDrawer, setShowAppointmentHistoryDrawer] = useState(false);
    const [recentlyBookedRefreshKey, setRecentlyBookedRefreshKey] = useState(0);
    const [showOrderDetail, setShowOrderDetail] = useState<{ orderId: number; bookingId: number } | null>(null);
    const pets = useSelector<RootState, PetsState>(state => state.pets);
    const petType = pets?.list?.find(pet => pet.id === props.petId)?.type?.plural?.toLowerCase();
    const roomsData = useSelector<RootState, Array<Room>>(state => state.rooms.roomsAvailableByPetId || []);

    const daycareServices = useSelector<RootState, Array<DaycareService>>(
        state => state.services.daycareServices || []
    );
    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);
    const hasDiscount = (daycareData.discount?.value ?? 0) > 0;
    const savedCustomPrice = useSavedCustomPriceChoice({
        petId: props.petId,
        serviceId: daycareData.service?.serviceId,
        catalogPrice: Number(daycareData.service?.beforePrice) || Number(daycareData.service?.price) || 0,
        currentPrice: Number(daycareData.price) || 0,
        useSavedCustomPrice: daycareData.useSavedCustomPrice
    });

    useEffect(() => {
        if (props.petId) {
            dispatch(fetchPetCustomPricesThunk(apiClientWrapper, props.petId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.petId]);

    useEffect(() => {
        if (!props.daycare) {
            return;
        }

        const syncSelectedDates = (daycare: DaycareModel) => {
            if (daycare.selectedDates?.length) {
                setSelectedDates(daycare.selectedDates.map(d => d.clone().tz(tz).startOf('day')));
            } else if (daycare.from?.isValid()) {
                setSelectedDates([daycare.from.clone().tz(tz).startOf('day')]);
            } else {
                setSelectedDates([]);
            }
        };

        if (!daycareLoaded) {
            setDaycareData(props.daycare);
            syncSelectedDates(props.daycare);
            setDaycareLoaded(true);
            return;
        }

        const incomingServiceId = props.daycare.service?.serviceId;
        const loadedServiceId = daycareData.service?.serviceId;
        if (incomingServiceId != null && loadedServiceId == null) {
            setDaycareData(props.daycare);
            syncSelectedDates(props.daycare);
        }
    }, [props.daycare, props.edit, tz, daycareLoaded, daycareData.service?.serviceId]);

    useEffect(() => {
        if (daycareServices && daycareServices?.length > 0 && daycareData.service?.serviceId) {
            const serviceWithLatePickupFee = daycareServices.find(
                service => service.serviceId === daycareData.service?.serviceId
            );
            if (serviceWithLatePickupFee?.latePickupFee && daycareData.service) {
                setDaycareData(prev => ({
                    ...prev,
                    service: {
                        ...prev.service!,
                        latePickupFee: serviceWithLatePickupFee.latePickupFee
                    }
                }));
            }
        }
    }, [daycareServices, daycareData.service?.serviceId]);

    useEffect(() => {
        if (roomsData && !props.daycare.room) {
            const selectedRoomByRoomUnitId = roomsData.find(room =>
                room.units?.some(unit => unit.id === daycareData.roomUnitId)
            );

            setDaycareData(prev => ({
                ...prev,
                room: selectedRoomByRoomUnitId
            }));
        }
    }, [roomsData]);

    useNonInitialEffect(() => {
        if (firstLoad) {
            setDaycareData(prev => ({
                ...prev,
                priceForHour: 0,
                discount: {
                    type: 'amount',
                    value: 0
                },
                room: undefined
            }));
            setFirstLoad(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [daycareData.service]);

    const notifyChange = (next: DaycareModel, dates: Array<Moment> = selectedDates) => {
        props.onChange?.(
            next,
            dates.map(d => d.clone().tz(tz).startOf('day'))
        );
    };

    const changePeriodHandler = (from: Moment, to: Moment) => {
        const newTo = from.clone().isBefore(to.clone())
            ? to.clone()
            : moment(getTodayClosingTime(from, marketplace)).tz(tz);
        const datesForEdit = [from.clone().tz(tz).startOf('day')];
        if (props.edit) {
            setSelectedDates(datesForEdit);
        }
        setDaycareData(prev => {
            const next = { ...prev, from: from.clone(), to: newTo.clone() };
            notifyChange(next, props.edit ? datesForEdit : undefined);
            return next;
        });
    };

    const changeSelectedDatesHandler = (datesOrUpdater: SetStateAction<Array<Moment>>) => {
        setSelectedDates(prev => {
            const next = typeof datesOrUpdater === 'function' ? datesOrUpdater(prev) : datesOrUpdater;
            const normalized = next.map(d => d.clone().tz(tz).startOf('day'));
            setDaycareData(current => {
                props.onChange?.(current, normalized);
                return current;
            });
            return normalized;
        });
    };

    const getAlerts = () => {
        if (!daycareData.from || !daycareData.to || !daycareData.service) {
            return [];
        }

        if (!daycareData.service.dropOffTime?.start || !daycareData.service.pickUpTime?.start) {
            return [];
        }

        const dropOffTime = daycareData.from.hour() * 100 + daycareData.from.minute();
        const pickUpTime = daycareData.to.hour() * 100 + daycareData.to.minute();

        const isDropOffInRange =
            daycareData.service.dropOffTime &&
            dropOffTime >= daycareData.service.dropOffTime.start &&
            dropOffTime <= daycareData.service.dropOffTime.end;

        const isPickUpInRange =
            daycareData.service.pickUpTime &&
            pickUpTime >= daycareData.service.pickUpTime.start &&
            pickUpTime <= daycareData.service.pickUpTime.end;

        if (isDropOffInRange && isPickUpInRange) {
            return [];
        }

        return [
            {
                alertType: 'date_time',
                message: 'Selected time is outside drop-off/pick-up time range',
                error: false
            }
        ];
    };

    return (
        <>
            <div className={styles.daycareSection}>
                {props.edit ? (
                    <PeriodSelector
                        from={daycareData.from}
                        to={daycareData.to}
                        labelDate="Date"
                        labelTimeStart="Drop Off"
                        labelTimeEnd="Pick Up"
                        isRecurrent={false}
                        frequencyOptions={[]}
                        alerts={getAlerts()}
                        onChangeFrequencyDuration={noop}
                        onChangePeriod={changePeriodHandler}
                    />
                ) : (
                    <MultipleDateSelector
                        from={daycareData.from}
                        to={daycareData.to}
                        selectedDates={selectedDates}
                        labelDate="Date"
                        labelTimeStart="Drop Off"
                        labelTimeEnd="Pick Up"
                        isRecurrent={false}
                        frequencyOptions={[]}
                        alerts={getAlerts()}
                        onChangeFrequencyDuration={noop}
                        onChangeSelectedDates={changeSelectedDatesHandler}
                        onChangePeriod={changePeriodHandler}
                    />
                )}
                <div className={styles.section}>
                    <RecentlyBooked
                        clientId={props.clientId}
                        petId={props.petId}
                        refreshTrigger={recentlyBookedRefreshKey}
                        onViewAll={() => setShowAppointmentHistoryDrawer(true)}
                        onAppointmentClick={(appointment: AppointmentListItem) => {
                            if (appointment.orderId != null && appointment.bookingId != null) {
                                setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                            }
                        }}
                    />
                </div>
                <DaycareServiceSelector
                    petId={props.petId}
                    selectedService={daycareData.service}
                    servicesSelected={props.servicesUsed || []}
                    onSelectService={(service: DaycareServiceWrapper) => {
                        setFirstLoad(true);
                        const catalogPrice = service.price || 0;
                        const sameService = daycareData.service?.serviceId === service.serviceId;
                        const keepEditedPrice = sameService && daycareData.price != null;
                        const resolved = resolveStandardPrice(catalogPrice, customPrices || [], service.serviceId || 0);
                        setDaycareData(prev => {
                            const next = {
                                ...prev,
                                service,
                                price: keepEditedPrice ? prev.price : resolved.price,
                                customPriceId: sameService
                                    ? prev.customPriceId ?? resolved.customPriceId
                                    : resolved.customPriceId,
                                useSavedCustomPrice: sameService ? prev.useSavedCustomPrice : undefined,
                                saveCustomPrice: sameService ? prev.saveCustomPrice : false
                            };
                            notifyChange(next);
                            return next;
                        });
                    }}
                />
                <div className={styles.priceHoursSection}>
                    {daycareData.service && (
                        <div className={styles.priceHour}>
                            <PriceHours
                                label="Price"
                                price={daycareData.price}
                                duration={daycareData.service?.durationService}
                                variableDuration={daycareData.service?.variableDuration}
                                onChangePrice={(price: number) =>
                                    setDaycareData(prev => {
                                        const next = {
                                            ...prev,
                                            price,
                                            ...(prev.service ? { service: { ...prev.service, price } } : {})
                                        };
                                        notifyChange(next);
                                        return next;
                                    })
                                }
                            />
                            {savedCustomPrice.show && (
                                <SavedCustomPriceChoice
                                    petName={savedCustomPrice.petName}
                                    savedPrice={savedCustomPrice.savedPrice}
                                    mode={savedCustomPrice.mode}
                                    disabled={!canEditPrices}
                                    onChange={mode => {
                                        const next = savedCustomPrice.applyMode(mode);
                                        setDaycareData(prev => {
                                            const updated = {
                                                ...prev,
                                                price: next.price,
                                                useSavedCustomPrice: next.useSavedCustomPrice,
                                                customPriceId: next.customPriceId
                                            };
                                            notifyChange(updated);
                                            return updated;
                                        });
                                    }}
                                />
                            )}
                            <SaveCustomPriceCheckbox
                                checked={!!daycareData.saveCustomPrice}
                                disabled={!canEditPrices}
                                currentPrice={daycareData.price}
                                defaultPrice={
                                    Number(daycareData.service?.beforePrice) || Number(daycareData.service?.price) || 0
                                }
                                onChange={checked =>
                                    setDaycareData(prev => {
                                        const next = { ...prev, saveCustomPrice: checked };
                                        notifyChange(next);
                                        return next;
                                    })
                                }
                            />
                        </div>
                    )}
                    {daycareData.service?.latePickupFee?.enabled && (
                        <div className={styles.priceHour}>
                            <label className={styles.label}>Charge for Additional Hours</label>
                            <span className={styles.additionalPrice}>
                                {`$${getFee(daycareData, petType)}  ${
                                    daycareData.service.latePickupFee?.type === 'per_hour' ? 'Hour' : 'Half Day'
                                }`}
                            </span>
                        </div>
                    )}
                </div>
                {daycareData.service && (
                    <div
                        className={clsx(styles.section, {
                            [lockStyles.interactionLock]: !canApplyDiscount && hasDiscount,
                            [lockStyles.hiddenComponent]: !canApplyDiscount && !hasDiscount
                        })}
                    >
                        <ValueTypeSelector
                            label="Discount"
                            value={daycareData.discount}
                            placeholder="$0.00"
                            max={daycareData.price}
                            className={styles.discount}
                            onChange={(value: ValueTypeSelectorValue) =>
                                setDaycareData(prev => {
                                    const next = { ...prev, discount: value };
                                    notifyChange(next);
                                    return next;
                                })
                            }
                        />
                    </div>
                )}
                {daycareData.service && (
                    <div className={styles.section}>
                        <RoomServiceSelector
                            petId={props.petId}
                            selectedRoom={daycareData.room}
                            serviceId={daycareData.service?.serviceId}
                            dropOffRoom={daycareData.from?.format()}
                            pickUpRoom={daycareData.to?.format()}
                            onSelectRoom={(room: Room) =>
                                setDaycareData(prev => {
                                    const roomUnitId =
                                        room.units?.find(u => u.available !== false)?.id ?? room.units?.[0]?.id;
                                    const next = { ...prev, room, roomUnitId };
                                    notifyChange(next);
                                    return next;
                                })
                            }
                        />
                    </div>
                )}
                <div className={styles.footerContainer}>
                    <Button
                        className={styles.buttonSave}
                        disabled={
                            !daycareData.service ||
                            !daycareData.room ||
                            !Number.isFinite(Number(daycareData.price)) ||
                            Number(daycareData.price) < 0
                        }
                        variant="green"
                        label="Next"
                        size="large"
                        onClick={() => {
                            const resolvedSelectedDates = props.edit
                                ? [daycareData.from.clone().tz(tz).startOf('day')]
                                : selectedDates.map(d => d.clone().tz(tz).startOf('day'));
                            const payload: DaycareModel = {
                                ...daycareData,
                                selectedDates: resolvedSelectedDates
                            };
                            if (props.enrollment != null) {
                                payload.enrollment = props.enrollment;
                            } else {
                                delete (payload as { enrollment?: unknown }).enrollment;
                            }
                            if (props.membershipItem != null) {
                                payload.membershipItem = props.membershipItem;
                            } else {
                                delete (payload as { membershipItem?: unknown }).membershipItem;
                            }
                            props.onNext(payload);
                        }}
                    />
                </div>
            </div>
            <AppointmentHistoryDrawer
                open={showAppointmentHistoryDrawer}
                onBack={() => {
                    setShowAppointmentHistoryDrawer(false);
                    setRecentlyBookedRefreshKey(k => k + 1);
                }}
                customerId={props.clientId}
                petId={props.petId}
                onAppointmentClick={(appointment: AppointmentListItem) => {
                    if (appointment.orderId != null && appointment.bookingId != null) {
                        setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                    }
                }}
            />
            {showOrderDetail && (
                <OrderDetail
                    orderId={showOrderDetail.orderId}
                    bookingId={showOrderDetail.bookingId}
                    readOnly={true}
                    onClose={() => setShowOrderDetail(null)}
                    zIndex={10100}
                />
            )}
        </>
    );
};

export default Daycare;
